import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Trash2, RefreshCw } from "lucide-react";
import { Toast } from "../../components/Toast";

interface PasswordHistoryItem {
  id: string;
  password: string;
  timestamp: number;
  strength: "weak" | "medium" | "strong";
}

const STORAGE_KEY = "peslarPasswordHistory";

const SIMILAR_CHARS = "il1Lo0O";
const AMBIGUOUS_CHARS = "{}[]()/'\"\\`~,;:.<>";

function generatePassword(options: {
  length: number;
  uppercase: boolean;
  lowercase: boolean;
  numbers: boolean;
  symbols: boolean;
  excludeSimilar: boolean;
  excludeAmbiguous: boolean;
  excludeExclamation: boolean;
}): string {
  let chars = "";

  if (options.uppercase) {
    chars += "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  }
  if (options.lowercase) {
    chars += "abcdefghijklmnopqrstuvwxyz";
  }
  if (options.numbers) {
    chars += "0123456789";
  }
  if (options.symbols) {
    let symbolSet = "!@#$%^&*()_+-=[]{}|;':\",./<>?`~";
    if (options.excludeExclamation) {
      symbolSet = symbolSet.replace("!", "");
    }
    chars += symbolSet;
  }

  if (options.excludeSimilar) {
    for (const c of SIMILAR_CHARS) {
      chars = chars.replace(new RegExp(c, "g"), "");
    }
  }

  if (options.excludeAmbiguous) {
    for (const c of AMBIGUOUS_CHARS) {
      chars = chars.replace(new RegExp("\\" + c, "g"), "");
    }
  }

  if (chars.length === 0) {
    return "";
  }

  const array = new Uint32Array(options.length);
  crypto.getRandomValues(array);

  let password = "";
  for (let i = 0; i < options.length; i++) {
    password += chars[array[i] % chars.length];
  }

  return password;
}

function calculateStrength(
  password: string,
  options: {
    uppercase: boolean;
    lowercase: boolean;
    numbers: boolean;
    symbols: boolean;
  }
): "weak" | "medium" | "strong" {
  const length = password.length;
  const typesCount = [
    options.uppercase,
    options.lowercase,
    options.numbers,
    options.symbols,
  ].filter(Boolean).length;

  if (length < 8 || typesCount < 2) {
    return "weak";
  }
  if (length >= 16 && typesCount >= 3) {
    return "strong";
  }
  if (length >= 12 && typesCount >= 2) {
    return "strong";
  }
  return "medium";
}

export function PasswordGenerator() {
  const { t } = useTranslation();

  const [password, setPassword] = useState("");
  const [length, setLength] = useState(28);
  const [uppercase, setUppercase] = useState(true);
  const [lowercase, setLowercase] = useState(true);
  const [numbers, setNumbers] = useState(true);
  const [symbols, setSymbols] = useState(true);
  const [excludeSimilar, setExcludeSimilar] = useState(true);
  const [excludeAmbiguous, setExcludeAmbiguous] = useState(true);
  const [excludeExclamation, setExcludeExclamation] = useState(true);
  const [saveToHistory, setSaveToHistory] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<PasswordHistoryItem[]>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        // Invalid JSON, return empty
      }
    }
    return [];
  });
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  }, [history]);

  const handleGenerate = useCallback(() => {
    const newPassword = generatePassword({
      length,
      uppercase,
      lowercase,
      numbers,
      symbols,
      excludeSimilar,
      excludeAmbiguous,
      excludeExclamation,
    });

    setPassword(newPassword);

    if (saveToHistory && newPassword) {
      const strength = calculateStrength(newPassword, {
        uppercase,
        lowercase,
        numbers,
        symbols,
      });

      const item: PasswordHistoryItem = {
        id: crypto.randomUUID(),
        password: newPassword,
        timestamp: Date.now(),
        strength,
      };

      setHistory((prev) => [item, ...prev]);
    }
  }, [
    length,
    uppercase,
    lowercase,
    numbers,
    symbols,
    excludeSimilar,
    excludeAmbiguous,
    excludeExclamation,
    saveToHistory,
  ]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setToastMessage(t("passwordGenerator.copied"));
    } catch {
      setToastMessage(t("passwordGenerator.copyFailed"));
    }
  };

  const deleteHistoryItem = (id: string) => {
    setHistory((prev) => prev.filter((item) => item.id !== id));
  };

  const clearHistory = () => {
    setHistory([]);
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const noCharSelected = !uppercase && !lowercase && !numbers && !symbols;

  return (
    <div className="password-generator">
      <div className="pg-output-row">
        <input
          type="text"
          className="pg-output"
          value={password}
          readOnly
          placeholder={t("passwordGenerator.placeholder")}
        />
        <button
          type="button"
          className="pg-copy-btn"
          onClick={() => copyToClipboard(password)}
          disabled={!password}
          title={t("passwordGenerator.copy")}
        >
          <Copy size={18} />
        </button>
      </div>

      <div className="pg-length">
        <label>
          {t("passwordGenerator.length")}: {length}
        </label>
        <input
          type="range"
          min={4}
          max={128}
          value={length}
          onChange={(e) => setLength(Number(e.target.value))}
          className="pg-slider"
        />
      </div>

      <div className="pg-settings-row">
        <div className="pg-section">
          <h3>{t("passwordGenerator.charTypes")}</h3>
          <label className="pg-checkbox">
            <input
              type="checkbox"
              checked={uppercase}
              onChange={(e) => setUppercase(e.target.checked)}
            />
            {t("passwordGenerator.uppercase")}
          </label>
          <label className="pg-checkbox">
            <input
              type="checkbox"
              checked={lowercase}
              onChange={(e) => setLowercase(e.target.checked)}
            />
            {t("passwordGenerator.lowercase")}
          </label>
          <label className="pg-checkbox">
            <input
              type="checkbox"
              checked={numbers}
              onChange={(e) => setNumbers(e.target.checked)}
            />
            {t("passwordGenerator.numbers")}
          </label>
          <label className="pg-checkbox">
            <input
              type="checkbox"
              checked={symbols}
              onChange={(e) => setSymbols(e.target.checked)}
            />
            {t("passwordGenerator.symbols")}
          </label>
        </div>

        <div className="pg-section">
          <h3>{t("passwordGenerator.securityOptions")}</h3>
          <label className="pg-checkbox">
            <input
              type="checkbox"
              checked={excludeSimilar}
              onChange={(e) => setExcludeSimilar(e.target.checked)}
            />
            {t("passwordGenerator.excludeSimilar")}
          </label>
          <label className="pg-checkbox">
            <input
              type="checkbox"
              checked={excludeAmbiguous}
              onChange={(e) => setExcludeAmbiguous(e.target.checked)}
            />
            {t("passwordGenerator.excludeAmbiguous")}
          </label>
          <label className="pg-checkbox">
            <input
              type="checkbox"
              checked={excludeExclamation}
              onChange={(e) => setExcludeExclamation(e.target.checked)}
            />
            {t("passwordGenerator.excludeExclamation")}
          </label>
        </div>
      </div>

      <button
        type="button"
        className="pg-generate-btn"
        onClick={handleGenerate}
        disabled={noCharSelected}
      >
        <RefreshCw size={18} />
        {t("passwordGenerator.generate")}
      </button>

      <div className="pg-history-toggle">
        <label className="pg-checkbox">
          <input
            type="checkbox"
            checked={saveToHistory}
            onChange={(e) => setSaveToHistory(e.target.checked)}
          />
          {t("passwordGenerator.saveToHistory")}
        </label>
      </div>

      <button
        type="button"
        className="pg-history-btn"
        onClick={() => setShowHistory((prev) => !prev)}
      >
        {showHistory
          ? t("passwordGenerator.hideHistory")
          : t("passwordGenerator.showHistory")}{" "}
        ({history.length})
      </button>

      {showHistory && (
        <div className="pg-history">
          <div className="pg-history-header">
            <h3>{t("passwordGenerator.history")}</h3>
            {history.length > 0 && (
              <button
                type="button"
                className="pg-clear-btn"
                onClick={clearHistory}
              >
                {t("passwordGenerator.clearHistory")}
              </button>
            )}
          </div>
          {history.length === 0 ? (
            <p className="pg-history-empty">
              {t("passwordGenerator.noHistory")}
            </p>
          ) : (
            <ul className="pg-history-list">
              {history.map((item) => (
                <li key={item.id} className="pg-history-item">
                  <div className="pg-history-main">
                    <code className="pg-history-password">{item.password}</code>
                    <div className="pg-history-meta">
                      <span className="pg-history-time">
                        {formatTimestamp(item.timestamp)}
                      </span>
                      <span className={`pg-strength pg-strength-${item.strength}`}>
                        {t(`passwordGenerator.strength.${item.strength}`)}
                      </span>
                    </div>
                  </div>
                  <div className="pg-history-actions">
                    <button
                      type="button"
                      className="pg-action-btn pg-action-copy"
                      onClick={() => copyToClipboard(item.password)}
                      title={t("passwordGenerator.copy")}
                    >
                      {t("passwordGenerator.copy")}
                    </button>
                    <button
                      type="button"
                      className="pg-action-btn pg-action-delete"
                      onClick={() => deleteHistoryItem(item.id)}
                      title={t("passwordGenerator.delete")}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
